import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  getCapabilitiesFromUser,
  getWarehouseSurfaceRequirementLabel,
  hasAnyCapability,
  hasCapability,
  isExternalSafetyReviewer,
  isProductEvaluator,
  canAccessWarehouseSurfaceForUser,
  type WarehouseSurfaceAccessKey,
} from '../auth/access';
import { hasMinRole, normalizeRole, type Role } from '../utils/roles';
import { buildLoginRedirectTo, destinationForAuthRedirect, rememberPostLoginPath } from '../utils/authRedirect';

export default function RoleProtectedRoute(props: {
  children: React.ReactNode;
  access?: WarehouseSurfaceAccessKey;
  minRole?: Role;
  capability?: string;
  anyCapability?: string[];
  /** Where to send users who don't have access. Default: "/" */
  redirectTo?: string;
}) {
  const { effectiveUser, isLoading } = useAuth();
  const location = useLocation();
  const caps = getCapabilitiesFromUser(effectiveUser);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!effectiveUser) {
    const next = destinationForAuthRedirect(location.pathname, location.search || '');
    rememberPostLoginPath(next);
    return <Navigate to={buildLoginRedirectTo(next)} replace />;
  }

  if (
    isExternalSafetyReviewer(effectiveUser) &&
    !isProductEvaluator(effectiveUser) &&
    !hasCapability(caps, 'incidents:read') &&
    props.access
  ) {
    return <Navigate to="/review" replace />;
  }

  if (props.access) {
    if (!canAccessWarehouseSurfaceForUser(effectiveUser, props.access)) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. Required access: <strong>{getWarehouseSurfaceRequirementLabel(props.access)}</strong>.
          </Alert>
        </Box>
      );
    }
  }

  if (props.capability) {
    if (!hasCapability(caps, props.capability)) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. Required capability: <strong>{props.capability}</strong>.
          </Alert>
        </Box>
      );
    }
  }

  if (props.anyCapability?.length) {
    if (!hasAnyCapability(caps, props.anyCapability)) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. Required one of: <strong>{props.anyCapability.join(', ')}</strong>.
          </Alert>
        </Box>
      );
    }
  }

  if (props.minRole) {
    const role = normalizeRole(effectiveUser.role);
    if (!hasMinRole(role, props.minRole)) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. Required role: <strong>{props.minRole}</strong>. Your role: <strong>{role}</strong>.
          </Alert>
        </Box>
      );
    }
  }

  return <>{props.children}</>;
}
